import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { IconLoader, IconAlert, IconPlus, IconTrash } from "../components/Icons";
import { includesFolded } from "../utils/text";
import { useMsg } from "../hooks/useMsg";

interface InventoryItem {
  id: string;
  product_id: string;
  warehouse_id: string;
  quantity: number;
  min_quantity: number | null;
  reserved_quantity?: number;
}

interface InventorySummary {
  product_id: string;
  product_name: string;
  product_code: string | null;
  warehouse_ids: string[];
  levels: InventoryItem[];
}

interface Warehouse {
  id: string;
  name: string;
  code: string;
  notification_time: string | null;
  notification_emails: string[];
}

const NOTIFICATION_TIMES = [
  { value: "", label: "Bez notifikace" },
  ...Array.from({ length: 24 }, (_, h) => {
    const hh = `${String(h).padStart(2, "0")}:00`;
    return { value: hh, label: hh };
  }),
];

export default function InventoryLevels() {
  const { user } = useCurrentUser();
  const canLimits = can(user, "stock_limits");
  const canCorrections = can(user, "stock_corrections");
  const canWarehouses = can(user, "warehouses", "write");
  const { t } = useTranslation();
  const [tab, setTab] = useState<"status" | "limits" | "notifications">("status");
  const [search, setSearch] = useState("");
  const [data, setData] = useState<InventorySummary[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [includeOpenOrders, setIncludeOpenOrders] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const setNotificationMsg = useMsg();
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<{ total: number; created: number; updated: number; errors: number; messages: string[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function load() {
    setLoading(true);
    try {
      const [summary, whList, profile] = await Promise.all([
        api.get<InventorySummary[]>("/inventory/summary").then(r => r.data),
        api.get<Warehouse[]>("/warehouses").then(r => r.data).catch(() => []),
        api.get<{ stock_include_open_orders: boolean }>("/tenant/profile").then(r => r.data).catch(() => null),
      ]);
      setData(summary);
      setWarehouses(whList);
      setIncludeOpenOrders(profile?.stock_include_open_orders ?? false);
    } catch (e) {
      setErr(apiError(e, t("inventory.loadError")));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (ext) form.append("format", ext);
    try {
      setLoading(true);
      setImportResult(null);
      const { data } = await api.post("/inventory/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      await load();
    } catch (e) {
      setErr(apiError(e, t("inventory.importError")));
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function downloadTemplate(format: "csv" | "xlsx") {
    const { data, headers } = await api.get(`/inventory/import/template?format=${format}`, {
      responseType: "blob",
    });
    const ct = typeof headers["content-type"] === "string" ? headers["content-type"] : (format === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
    const blob = new Blob([data], { type: ct });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `luigipos_inventar_sablona.${format}`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  function getLevel(productId: string, warehouseId: string): InventoryItem | undefined {
    const product = data.find(p => p.product_id === productId);
    return product?.levels.find(l => l.warehouse_id === warehouseId);
  }

  function updateLocalLevel(productId: string, warehouseId: string, field: "quantity" | "min_quantity", value: number | null) {
    setData(prev => prev.map(p => {
      if (p.product_id !== productId) return p;
      return {
        ...p,
        levels: p.levels.map(l => l.warehouse_id === warehouseId ? { ...l, [field]: value } : l),
      };
    }));
  }

  async function saveLimits() {
    const rows = data.flatMap(p => p.levels.map(l => ({
      id: l.id,
      min_quantity: l.min_quantity,
    })));
    setSaving(true);
    try {
      await api.post("/inventory/bulk", { rows });
      await load();
    } catch (e) {
      setErr(apiError(e, t("inventory.saveError")));
    } finally {
      setSaving(false);
    }
  }

  function setWarehouseNotification(warehouseId: string, patch: Partial<Warehouse>) {
    setWarehouses(prev => prev.map(w => w.id === warehouseId ? { ...w, ...patch } : w));
  }

  function addNotificationEmail(warehouseId: string) {
    setWarehouses(prev => prev.map(w => w.id === warehouseId ? { ...w, notification_emails: [...w.notification_emails, ""] } : w));
  }

  function updateNotificationEmail(warehouseId: string, idx: number, email: string) {
    setWarehouses(prev => prev.map(w => {
      if (w.id !== warehouseId) return w;
      const emails = [...w.notification_emails];
      emails[idx] = email;
      return { ...w, notification_emails: emails };
    }));
  }

  function removeNotificationEmail(warehouseId: string, idx: number) {
    setWarehouses(prev => prev.map(w => w.id === warehouseId ? { ...w, notification_emails: w.notification_emails.filter((_, i) => i !== idx) } : w));
  }

  async function saveNotifications() {
    setSaving(true);
    setNotificationMsg(null);
    try {
      await Promise.all(warehouses.map(w =>
        api.put(`/warehouses/${w.id}/notification`, {
          notification_time: w.notification_time || null,
          notification_emails: w.notification_emails.filter(e => e.trim() !== ""),
        })
      ));
      await load();
      setNotificationMsg({ type: "ok", text: t("inventory.notifSaved") });
    } catch {
      setNotificationMsg({ type: "err", text: t("inventory.notifError") });
    } finally {
      setSaving(false);
    }
  }

  const needle = search.trim();
  const filteredData = needle
    ? data.filter(p =>
        includesFolded(p.product_name, needle) ||
        includesFolded(p.product_code ?? "", needle)
      )
    : data;

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>;

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1>{t("inventory.title")}</h1>
          <p>{t("inventory.subtitle")}</p>
        </div>
        {canCorrections && (
        <div style={{ position: "relative" }}>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            ref={fileInputRef}
            onChange={handleImportFile}
            style={{ display: "none" }}
          />
          <div style={{ display: "flex" }}>
            <button
              className="primary"
              style={{ borderRadius: "var(--radius-sm) 0 0 var(--radius-sm)", borderRight: "1px solid rgba(255,255,255,0.25)" }}
              onClick={() => fileInputRef.current?.click()}
            >
              {t("inventory.importInventory")}
            </button>
            <button
              className="primary"
              style={{ borderRadius: "0 var(--radius-sm) var(--radius-sm) 0", padding: "0 10px" }}
              onClick={() => setImportOpen(!importOpen)}
            >
              <span style={{ fontSize: 10 }}>▼</span>
            </button>
          </div>
          {importOpen && (
            <div
              style={{
                position: "absolute", top: "100%", right: 0, marginTop: 4,
                minWidth: 220, borderRadius: "var(--radius-sm)", border: "1px solid var(--border)",
                background: "var(--bg-elevated)", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 100,
              }}
            >
              <div
                style={{ padding: "10px 14px", fontSize: 14, cursor: "pointer", borderBottom: "1px solid var(--border)" }}
                onClick={() => { setImportOpen(false); downloadTemplate("csv"); }}
              >
                {t("inventory.downloadCsvTemplate")}
              </div>
              <div
                style={{ padding: "10px 14px", fontSize: 14, cursor: "pointer" }}
                onClick={() => { setImportOpen(false); downloadTemplate("xlsx"); }}
              >
                {t("inventory.downloadXlsxTemplate")}
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {err && <div className="card" style={{ padding: 12, marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8 }}><IconAlert size={16} /> {err}</div>}

      {importResult && (
        <div className="card" style={{ padding: 16, marginBottom: 20, background: "var(--green-dim)", color: "var(--green)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <strong>{t("inventory.importResultTitle")}</strong>
            <button className="ghost" onClick={() => setImportResult(null)} style={{ color: "var(--green)", padding: "4px 8px" }}>×</button>
          </div>
          <p style={{ fontSize: 14, margin: 0 }}>
            {t("inventory.importResultSummary", { total: importResult.total, created: importResult.created, updated: importResult.updated, errors: importResult.errors })}
          </p>
          {importResult.messages.length > 0 && (
            <ul style={{ fontSize: 13, margin: "8px 0 0", paddingLeft: 18 }}>
              {importResult.messages.slice(0, 10).map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid var(--border)" }}>
        {[
          { id: "status", label: t("inventory.statusTab") },
          ...(canLimits ? [{ id: "limits", label: t("inventory.limitsTab") }] : []),
          ...(canWarehouses ? [{ id: "notifications", label: t("inventory.notificationsTab") }] : []),
        ].map(item => (
          <button
            key={item.id}
            onClick={() => setTab(item.id as "status" | "limits" | "notifications")}
            style={{
              padding: "10px 18px", border: "none", background: "transparent",
              color: tab === item.id ? "var(--accent)" : "var(--muted)",
              fontWeight: tab === item.id ? 600 : 500, fontSize: 14,
              borderBottom: tab === item.id ? "2px solid var(--accent)" : "2px solid transparent",
              cursor: "pointer", marginBottom: -1,
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab !== "notifications" && (
        <div style={{ marginBottom: 16, maxWidth: 360 }}>
          <input
            placeholder={t("inventory.searchProduct")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
      )}

      {tab === "notifications" ? (
        <div className="card" style={{ overflow: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>{t("common.warehouse")}</th>
                <th>{t("inventory.notifTime")}</th>
                <th>{t("inventory.extraEmails")}</th>
              </tr>
            </thead>
            <tbody>
              {warehouses.map(w => (
                <tr key={w.id}>
                  <td style={{ fontWeight: 600 }}>{w.name}</td>
                  <td>
                    <select
                      disabled={!canWarehouses}
                      value={w.notification_time || ""}
                      onChange={e => setWarehouseNotification(w.id, { notification_time: e.target.value || null })}
                      style={{ minWidth: 160 }}
                    >
                      {NOTIFICATION_TIMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </td>
                  <td style={{ minWidth: 320 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {w.notification_emails.map((email, idx) => (
                        <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input
                            type="email"
                            disabled={!canWarehouses}
                            value={email}
                            onChange={e => updateNotificationEmail(w.id, idx, e.target.value)}
                            placeholder="manager@firma.cz"
                            style={{ flex: 1 }}
                          />
                          <button type="button" className="ghost" disabled={!canWarehouses} style={{ color: "var(--red)", padding: "6px 8px" }} onClick={() => removeNotificationEmail(w.id, idx)}>
                            <IconTrash size={16} />
                          </button>
                        </div>
                      ))}
                      <button type="button" className="ghost" disabled={!canWarehouses} style={{ justifyContent: "flex-start", fontSize: 13 }} onClick={() => addNotificationEmail(w.id)}>
                        <IconPlus size={14} /> Přidat adresu
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: 16, borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end" }}>
            <button className="primary" onClick={saveNotifications} disabled={saving || !canWarehouses}>{saving ? t("common.saving") : t("inventory.saveNotifications")}</button>
          </div>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--muted)" }}>{t("inventory.noProducts")}</div>
      ) : (
        <div className="card" style={{ overflow: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>{t("inventory.product")}</th>
                <th></th>
                {warehouses.map(w => <th key={w.id}>{w.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {filteredData.map(p => (
                <tr key={p.product_id}>
                  <td style={{ fontWeight: 600, minWidth: 200 }}>{p.product_name} <span style={{ color: "var(--muted)", fontWeight: 400, fontSize: 12 }}>{p.product_code || ""}</span></td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Link to={`/inventory/movements?product=${p.product_id}`} className="ghost" style={{ fontSize: 13, padding: "4px 10px", textDecoration: "none" }}>
                      {t("nav.stockMovements")}
                    </Link>
                  </td>
                  {warehouses.map(w => {
                    const level = getLevel(p.product_id, w.id);
                    const allowed = p.warehouse_ids.includes(w.id);
                    if (!allowed) {
                      return (
                        <td key={w.id} title={t("inventory.notAssigned")} style={{ color: "var(--muted)", textAlign: "center", fontSize: 18, cursor: "help" }}>
                          ✕
                        </td>
                      );
                    }
                    if (tab === "status") {
                      const reserved = includeOpenOrders ? (level?.reserved_quantity ?? 0) : 0;
                      const available = level ? level.quantity - reserved : null;
                      const low = level && level.min_quantity !== null && (available ?? level.quantity) <= level.min_quantity;
                      const fmtQty = (n: number) => parseFloat(n.toFixed(3));
                      return (
                        <td key={w.id} style={{
                          background: low ? "var(--red-dim)" : "transparent",
                          color: low ? "var(--red)" : "var(--text)",
                          fontWeight: low ? 700 : 400,
                        }}
                          title={level && reserved > 0 ? t("inventory.reservedTip", { qty: fmtQty(level.quantity), reserved: fmtQty(reserved) }) : undefined}
                        >
                          {available !== null ? fmtQty(available) : "—"}
                        </td>
                      );
                    }
                    return (
                      <td key={w.id}>
                        {level ? (
                          <input
                            type="number"
                            min={0}
                            step="0.001"
                            value={level.min_quantity ?? ""}
                            disabled={!canLimits}
                            onChange={e => updateLocalLevel(p.product_id, w.id, "min_quantity", e.target.value === "" ? null : parseFloat(e.target.value))}
                            style={{ width: 100, textAlign: "right" }}
                          />
                        ) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {tab === "limits" && (
            <div style={{ padding: 16, borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end" }}>
              <button className="primary" onClick={saveLimits} disabled={saving || !canLimits}>{saving ? t("common.saving") : t("inventory.saveLimits")}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
